import { Module } from '../../types';

export const dspyModule1: Module = {
  id: 'dspy-module1',
  title: 'Introduction to DSPy',
  description:
    'Learn the fundamentals of DSPy (Declarative Self-improving Python), understand its philosophy of programming vs prompting, set up your development environment, and build your first DSPy program.',
  duration: '2-3 hours',
  content: `# Module 1: Introduction to DSPy

## 🎯 Learning Objectives

By the end of this module, you will:
- Understand the fundamental philosophy behind DSPy and how it differs from traditional prompting
- Identify when and why to use DSPy over conventional approaches
- Set up a complete DSPy development environment
- Build and run your first DSPy program
- Understand the core components that make up DSPy applications

## 📚 What is DSPy?

### The Evolution of AI Development

Imagine you're building a customer support chatbot for an e-commerce company. With traditional approaches, you'd spend weeks crafting the perfect prompts, testing edge cases, and manually tweaking responses. A small change in wording could break your entire system, and scaling to handle different types of queries would require starting from scratch each time.

This is the reality most AI developers face today. The current landscape of LLM development is characterized by:

### The Problem with Traditional Prompting

**Manual prompt engineering** has become a bottleneck in AI development. Teams spend countless hours writing, testing, and tweaking prompts by hand. A data scientist at a major tech company recently shared: "We had a team of three engineers spending 40% of their time just maintaining prompts. Every time we wanted to add a new feature, we had to rewrite everything."

**Fragile systems** are the norm. Small changes in prompt wording can cause dramatic shifts in behavior. Companies have reported production failures where a single word change caused their AI system to start hallucinating or refusing to answer legitimate questions.

**Poor modularity** makes it nearly impossible to compose and reuse components. If you build a great summarization prompt for one use case, adapting it for another often means starting over completely.

**Limited optimization** means there's no systematic way to improve performance. Teams resort to A/B testing prompts manually, which is time-consuming and doesn't scale.

### The DSPy Solution: A New Paradigm

DSPy (Declarative Self-improving Python) represents a fundamental shift in how we think about AI development. Instead of crafting prompts, you declare what you want your system to do, and DSPy handles the rest.

Consider this transformation:

\`\`\`python
# Traditional Prompting
prompt = """Given the question below, answer it clearly and concisely.
Question: {question}
Answer:"""
response = llm.complete(prompt.format(question="What is the capital of France?"))

# DSPy Approach
qa = dspy.Predict("question -> answer")
response = qa(question="What is the capital of France?")
\`\`\`

### Core Philosophy: Programming, Not Prompting

The breakthrough insight behind DSPy is deceptively simple: treat language model interactions like any other programming interface. Just as you don't write assembly code when building a web application, you shouldn't have to craft prompts when building AI systems.

DSPy transforms LLM calls from **text completions** into **function calls** with clear interfaces:

**1. Declarative Specifications** - You define what you want, not how to get it. Instead of writing "Given the following context, please carefully analyze it and provide a comprehensive summary that captures the key points while maintaining accuracy...", you simply declare: \`document -> summary\`.

**2. Automatic Prompt Generation** - DSPy creates optimal prompts for you based on your specifications and data. The system learns what works best for your specific use case, automatically handling the nuances of prompt engineering.

**3. Composable Modules** - Build complex systems from simple, reusable components. A document analysis system might combine retrieval, summarization, fact-checking, and sentiment analysis modules, each optimized independently but working together seamlessly.

**4. Systematic Optimization** - Improve performance through data and automatic optimization techniques, not manual tweaking. Instead of guessing what might work better, DSPy uses your training data to systematically improve the system's performance.

### Key Benefits

#### 1. **Modularity**
\`\`\`python
# Compose modules like Lego blocks
class RAGPipeline(dspy.Module):
    def __init__(self):
        self.retrieve = dspy.Retrieve(k=3)
        self.generate = dspy.ChainOfThought("context, question -> answer")
    
    def forward(self, question):
        context = self.retrieve(question).passages
        return self.generate(context=context, question=question)
\`\`\`

#### 2. **Portability**
- Same code works across different LLMs (GPT-4, Claude, Llama, etc.)
- No need to rewrite prompts for each model
- Automatic adaptation to model-specific formats

#### 3. **Optimization**
\`\`\`python
# Optimize your program with data, not manual prompt engineering
optimizer = dspy.BootstrapFewShot(metric=my_metric)
optimized_program = optimizer.compile(program, trainset=train_data)
\`\`\`

#### 4. **Maintainability**
- Version control for AI behaviors
- Unit testing for LLM applications
- Clear separation of logic and implementation

### When to Use DSPy

**DSPy shines in scenarios where traditional prompting falls short:**

**✅ Production AI systems requiring reliability** - When your AI system needs to work consistently across thousands of users and diverse inputs, DSPy's systematic approach to optimization and error handling becomes invaluable. A fintech company recently migrated their fraud detection system to DSPy and saw a 40% reduction in false positives.

**✅ Complex multi-stage pipelines** - If your system involves multiple steps (retrieve information, analyze it, summarize findings, generate recommendations), DSPy's modular architecture makes it far easier to develop, test, and maintain each component independently.

**✅ Applications needing systematic improvement** - When you have performance metrics and want to continuously improve your system, DSPy's optimizers can automatically find better configurations using your data.

**✅ Teams wanting to collaborate on AI development** - DSPy code is more maintainable and readable than complex prompt chains. Team members can contribute to different modules without understanding the entire system.

**✅ Projects requiring model portability** - If you need to switch between different LLMs (perhaps for cost, performance, or compliance reasons), DSPy handles the model-specific formatting automatically.

**⚠️ Consider alternatives when:**
- Building quick prototypes or one-off experiments where development speed matters more than maintainability
- You have very specific prompt formatting requirements that can't be expressed declaratively
- Working with cutting-edge prompt techniques that DSPy doesn't yet support
- Your entire use case is a single, simple API call with no need for optimization

### Real-World Success Stories

**Information Extraction at Scale** - A legal tech startup uses DSPy to extract key information from contracts. Their system processes thousands of documents daily, automatically identifying parties, dates, obligations, and risks. What previously required a team of paralegals now runs automatically with 95% accuracy.

**Multi-hop Question Answering** - A research institution built a DSPy system that answers complex scientific questions by retrieving relevant papers, reasoning about the findings, and synthesizing coherent answers. The system can handle questions like "How do recent advances in CRISPR technology relate to potential treatments for Huntington's disease?"

**Content Generation with Citations** - A news organization uses DSPy to generate article summaries that automatically include proper citations. The system retrieves relevant sources, synthesizes the information, and formats it according to their style guide – all while maintaining accuracy and avoiding hallucinations.

**Intelligent Classification** - An e-commerce platform employs DSPy for product categorization. The system doesn't just classify products into predefined categories; it can create new subcategories when needed and explain its reasoning, helping merchants better organize their inventory.

**Tool-using AI Assistants** - A software company built a DSPy-powered coding assistant that can analyze code, suggest improvements, run tests, and even make pull requests. The system combines multiple tools (code analysis, documentation lookup, test execution) in a coherent workflow.

## 🔧 Core Concepts Overview

### The DSPy Stack

\`\`\`mermaid
flowchart TB
    A[" <br/><b>Your Application</b><br/>Domain-specific implementation<br/> "]
    B[" <br/><b>DSPy Modules</b><br/>Custom logic, composition, and workflows<br/> "]
    C[" <br/><b>DSPy Predictors</b><br/>Predict, ChainOfThought, ReAct, ProgramOfThought<br/> "]
    D[" <br/><b>DSPy Signatures</b><br/>Input/Output specifications with type safety<br/> "]
    E[" <br/><b>DSPy Optimizers</b><br/>BootstrapFewShot, MIPRO, COPRO<br/> "]
    F[" <br/><b>Language Model Adapters</b><br/>Model-specific formatting and API integration<br/> "]
    G[" <br/><b>Language Models</b><br/>GPT-4, Claude, Llama, Gemini, etc.<br/> "]
    
    A ==> B
    B ==> C
    C ==> D
    D ==> E
    E ==> F
    F ==> G
    
    style A fill:#0d47a1,color:#fff,stroke:#0d47a1,stroke-width:0px
    style B fill:#1565c0,color:#fff,stroke:#1565c0,stroke-width:0px  
    style C fill:#1976d2,color:#fff,stroke:#1976d2,stroke-width:0px
    style D fill:#1e88e5,color:#fff,stroke:#1e88e5,stroke-width:0px
    style E fill:#2196f3,color:#fff,stroke:#2196f3,stroke-width:0px
    style F fill:#42a5f5,color:#fff,stroke:#42a5f5,stroke-width:0px
    style G fill:#64b5f6,color:#000,stroke:#64b5f6,stroke-width:0px
\`\`\`

### 1. Signatures: Declaring What You Want

Signatures define the input/output interface for LM calls:

\`\`\`python
# Inline signature syntax
"question -> answer"
"document -> summary"
"context, question -> answer"
"text -> entities: list[str]"

# Class-based signatures for complex tasks
class SentimentAnalysis(dspy.Signature):
    """Analyze the sentiment of customer feedback."""
    
    feedback: str = dspy.InputField(desc="Customer feedback text")
    sentiment: Literal['positive', 'negative', 'neutral'] = dspy.OutputField()
    confidence: float = dspy.OutputField(desc="Confidence score between 0 and 1")
    key_phrases: list[str] = dspy.OutputField(desc="Important phrases affecting sentiment")
\`\`\`

### 2. Modules: Building Blocks of AI Systems

Modules encapsulate AI behaviors into reusable components:

\`\`\`python
# Using built-in modules
predict = dspy.Predict("question -> answer")
cot = dspy.ChainOfThought("question -> answer")  # Adds reasoning
react = dspy.ReAct("question -> answer", tools=[search, calculate])  # Uses tools

# Creating custom modules
class FactChecker(dspy.Module):
    def __init__(self):
        self.search = dspy.Retrieve(k=5)
        self.verify = dspy.ChainOfThought("claim, evidence -> verdict: bool, explanation")
    
    def forward(self, claim):
        evidence = self.search(claim).passages
        return self.verify(claim=claim, evidence=evidence)
\`\`\`

### 3. Optimizers: Teaching LMs to Perform Better

Optimizers automatically improve your programs:

\`\`\`python
# Define what "good" means
def accuracy_metric(gold, pred, trace=None):
    return gold.answer.lower() == pred.answer.lower()

# Let DSPy find the best prompts/examples
optimizer = dspy.BootstrapFewShot(metric=accuracy_metric)
optimized_qa = optimizer.compile(qa_program, trainset=training_data)

# Types of optimizers:
# - LabeledFewShot: Use hand-labeled examples
# - BootstrapFewShot: Generate examples automatically
# - MIPRO: Optimize instructions and demonstrations
# - COPRO: Coordinate ascent for prompt optimization
\`\`\`

### 4. The Development Workflow

\`\`\`mermaid
flowchart TB
    subgraph Define
        A1[Create Signature] --> A2[Define Input/Output Fields]
        A2 --> A3[Add Type Hints & Descriptions]
    end
    
    subgraph Build
        B1[Choose Predictor] --> B2[Create Module]
        B2 --> B3[Compose Modules]
    end
    
    subgraph Optimize
        C1[Prepare Dataset] --> C2[Define Metric]
        C2 --> C3[Select Optimizer]
        C3 --> C4[Compile Program]
    end
    
    subgraph Deploy
        D1[Save Optimized Program] --> D2[Load in Production]
        D2 --> D3[Monitor Performance]
    end
    
    Define --> Build
    Build --> Optimize
    Optimize --> Deploy
    
    style Define fill:#e3f2fd
    style Build fill:#f3e5f5
    style Optimize fill:#fff3e0
    style Deploy fill:#e8f5e9
\`\`\`

\`\`\`python
# Step 1: Define your task
class MathQA(dspy.Signature):
    """Solve math word problems step by step."""
    question: str = dspy.InputField()
    reasoning: str = dspy.OutputField(desc="Step-by-step solution")
    answer: float = dspy.OutputField()

# Step 2: Create your program
math_solver = dspy.ChainOfThought(MathQA)

# Step 3: Prepare data (even just 20-50 examples)
trainset = [
    dspy.Example(question="If x + 2 = 5, what is x?", answer=3),
    # ... more examples
]

# Step 4: Define success metric
def math_metric(gold, pred, trace=None):
    return abs(gold.answer - pred.answer) < 0.01

# Step 5: Optimize
optimizer = dspy.BootstrapFewShot(metric=math_metric)
optimized_solver = optimizer.compile(math_solver, trainset=trainset)

# Step 6: Use in production
result = optimized_solver(question="A train travels 60 mph for 2 hours. How far did it go?")
print(f"Answer: {result.answer} miles")  # Answer: 120.0 miles
\`\`\`

### Key Takeaways

1. **Signatures** = What you want (interface)
2. **Modules** = How to do it (logic)
3. **Optimizers** = Make it better (learning)
4. **Programs** = Modules + Optimizers (complete system)

## ⚙️ Setting Up Your Environment

Getting started with DSPy is straightforward, but there are some best practices that will save you time and frustration later. This section will guide you through setting up a robust development environment that scales from experimentation to production.

### System Requirements

**Hardware Considerations:**
- **Python**: 3.9 or higher (3.11+ strongly recommended for better performance)
- **Memory**: 4GB RAM minimum for basic usage, 8GB+ recommended when using optimizers with large datasets
- **Disk Space**: 1GB for core dependencies, but plan for 5-10GB if you'll be caching responses and storing model outputs
- **Internet**: Stable connection required for API-based models; bandwidth isn't critical but latency matters for interactive development

**Operating System Support:**
DSPy works seamlessly on macOS, Linux, and Windows. However, if you're planning to use local models or advanced optimization techniques, Linux or macOS will generally provide better performance and fewer compatibility issues.

### Installation Strategies

The way you install DSPy depends on your goals and experience level. Here are the most common approaches:

#### Option 1: Basic Installation (Recommended for beginners)

This is perfect if you're just getting started and want to experiment with DSPy's core features:

\`\`\`bash
# Using pip (traditional approach)
pip install -U dspy

# Or using uv (faster, modern approach - recommended)
uv pip install dspy

# Verify installation
python -c "import dspy; print(f'DSPy version: {dspy.__version__}')"
\`\`\`

#### Option 2: Virtual Environment Setup (Recommended for most users)

Isolating your DSPy environment prevents conflicts with other projects:

\`\`\`bash
# Create a dedicated virtual environment
python -m venv dspy-env
source dspy-env/bin/activate  # On Windows: dspy-env\\Scripts\\activate

# Install DSPy
pip install -U dspy

# Optional: Install common additional packages
pip install jupyter notebook pandas matplotlib
\`\`\`

#### Option 3: Development Installation (For contributors and advanced users)

If you want to contribute to DSPy or need the latest features:

\`\`\`bash
# Clone the repository
git clone https://github.com/stanfordnlp/dspy.git
cd dspy

# Install with development dependencies
pip install -e ".[dev]"

# Or with uv (faster)
uv sync --extra dev

# Run tests to verify everything works
python -m pytest tests/
\`\`\`

### Configuring Language Models

#### 1. OpenAI Models

\`\`\`python
import dspy
import os

# Option A: Environment variable
os.environ["OPENAI_API_KEY"] = "your-api-key"
lm = dspy.LM('openai/gpt-4o-mini')

# Option B: Direct specification
lm = dspy.LM('openai/gpt-4o-mini', api_key='your-api-key')

# Configure DSPy to use this model
dspy.configure(lm=lm)
\`\`\`

#### 2. Anthropic Claude

\`\`\`python
# Requires: pip install anthropic
lm = dspy.LM('anthropic/claude-3-5-sonnet-20241022', 
            api_key='your-anthropic-key')
dspy.configure(lm=lm)
\`\`\`

#### 3. Local Models with Ollama

\`\`\`bash
# First, install and start Ollama
# Download from: https://ollama.ai

# Pull a model
ollama pull llama3.2

# In Python:
lm = dspy.LM('ollama_chat/llama3.2', 
            api_base='http://localhost:11434', 
            api_key='')  # No key needed for local
dspy.configure(lm=lm)
\`\`\`

#### 4. Other Providers via LiteLLM

\`\`\`python
# Gemini
lm = dspy.LM('gemini/gemini-1.5-flash', api_key='your-key')

# Cohere
lm = dspy.LM('cohere/command-r-plus', api_key='your-key')

# Any LiteLLM-supported model
lm = dspy.LM('provider/model-name', api_key='your-key')
\`\`\`

### Essential Configuration

\`\`\`python
import dspy

# Basic setup
dspy.configure(
    lm=dspy.LM('openai/gpt-4o-mini'),  # Your chosen model
    # Optional configurations:
    rm=dspy.ColBERTv2(url='http://20.102.90.50:2017/wiki17_abstracts'),  # Retriever
    trace=[],  # Enable trace tracking
    explain=False  # Disable explanations for production
)

# Environment-specific settings
dspy.settings.configure(
    experimental=True,  # Enable experimental features
    track_usage=True,   # Track token usage
    return_all_scores=True  # For debugging
)
\`\`\`

### Setting Up Caching (Recommended)

\`\`\`python
# In-memory caching (default)
# Automatically enabled

# Disk-based caching for persistence
os.environ["DSP_CACHEDIR"] = "./cache"

# Or use LiteLLM cache
from litellm import Cache
import litellm

litellm.cache = Cache(
    type="disk",
    disk_cache_dir="./litellm_cache"
)
\`\`\`

### Verifying Your Installation

Create a file \`test_dspy.py\`:

\`\`\`python
import dspy

# Configure with your model
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

# Test basic functionality
qa = dspy.Predict("question -> answer")
result = qa(question="What is 2 + 2?")
print(f"Answer: {result.answer}")

# Test Chain of Thought
cot = dspy.ChainOfThought("question -> answer")
result = cot(question="Why is the sky blue?")
print(f"\\nReasoning: {result.reasoning}")
print(f"Answer: {result.answer}")

# Check version
print(f"\\nDSPy version: {dspy.__version__}")
\`\`\`

Run it:
\`\`\`bash
python test_dspy.py
\`\`\`

Expected output:
\`\`\`
Answer: 4

Reasoning: The sky appears blue due to a phenomenon called Rayleigh scattering...
Answer: The sky is blue because molecules in Earth's atmosphere scatter short-wavelength light from the sun more than long-wavelength light.

DSPy version: 3.0.x
\`\`\`

### Troubleshooting Common Issues

#### 1. Import Errors
\`\`\`bash
# Ensure you're in the right environment
which python
pip list | grep dspy
\`\`\`

#### 2. API Key Issues
\`\`\`python
# Debug API configuration
print(dspy.settings.lm.api_key[:8] + "...")  # Check key is loaded
\`\`\`

#### 3. Connection Errors
\`\`\`python
# Test with a simple direct call
try:
    response = dspy.settings.lm("Test")
    print("LM connection successful")
except Exception as e:
    print(f"Error: {e}")
\`\`\`

### Development Tools Setup

For the full course, also install:

\`\`\`bash
# Jupyter for interactive development
pip install jupyter notebook

# Code quality tools
pip install black isort pylint

# Testing framework
pip install pytest pytest-asyncio

# Visualization
pip install matplotlib seaborn

# Data handling
pip install pandas datasets
\`\`\`

### Next Steps

With your environment ready, you can:
1. Explore the example notebooks in \`docs/tutorials/\`
2. Run the test suite: \`pytest tests/\`
3. Start building your first DSPy program!

## 🚀 Your First DSPy Program

The best way to understand DSPy is to build something meaningful with it. In this section, we'll create a progressively sophisticated question-answering system that demonstrates DSPy's core principles. By the end, you'll have built a system that could serve as the foundation for a production chatbot or knowledge assistant.

### Project: Building a Smart Q&A System

Our journey will take us from a simple Q&A function to a sophisticated system that:
1. **Handles diverse question types** - from simple facts to complex reasoning
2. **Provides transparent reasoning** - users can see how the system arrived at its answer
3. **Integrates external knowledge** - uses web search when needed for current information
4. **Improves automatically** - gets better through optimization without manual tuning
5. **Adapts to new domains** - can be easily customized for specific use cases

This isn't just a toy example – similar systems power customer support bots at major companies, research assistants at universities, and knowledge management tools at enterprises.

### The Evolution: From Simple to Sophisticated

We'll build our system in stages, each revealing new DSPy capabilities:

### Step 1: Basic Question Answering

#### Traditional Approach
\`\`\`python
import openai

def traditional_qa(question):
    prompt = f"""You are a helpful assistant. Answer the following question accurately and concisely.

Question: {question}
Answer:"""
    
    response = openai.chat.completions.create(
        model="gpt-4o-mini",
        messages=[{"role": "user", "content": prompt}]
    )
    return response.choices[0].message.content

# Usage
answer = traditional_qa("What is the capital of France?")
print(answer)  # "The capital of France is Paris."
\`\`\`

#### DSPy Approach
\`\`\`python
import dspy

# Configure once
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

# Create a reusable QA module
qa = dspy.Predict("question -> answer")

# Usage
result = qa(question="What is the capital of France?")
print(result.answer)  # "Paris"
\`\`\`

**Key Differences:**
- No manual prompt engineering
- Structured output (result.answer)
- Reusable module
- Automatic error handling

### Step 2: Adding Chain of Thought Reasoning

#### DSPy Approach
\`\`\`python
# Simply switch to ChainOfThought
cot_qa = dspy.ChainOfThought("question -> answer")

result = cot_qa(question="If a train travels 120 miles in 2 hours, what is its average speed?")
print(f"Reasoning: {result.reasoning}")
print(f"Answer: {result.answer}")
\`\`\`

Output:
\`\`\`
Reasoning: To find average speed, I need to divide total distance by total time. 
The train travels 120 miles in 2 hours. Speed = Distance / Time = 120 miles / 2 hours = 60 miles per hour.
Answer: 60 miles per hour
\`\`\`

### Step 3: Building a Complete RAG System

\`\`\`python
class SimpleRAG(dspy.Module):
    """A simple Retrieval-Augmented Generation system."""
    
    def __init__(self, k=3):
        # Initialize sub-modules
        self.retrieve = dspy.Retrieve(k=k)
        self.generate = dspy.ChainOfThought("context, question -> answer")
    
    def forward(self, question):
        # Retrieve relevant passages
        retrieval_result = self.retrieve(question)
        context = retrieval_result.passages
        
        # Generate answer using context
        return self.generate(context=context, question=question)

# Create and use the RAG system
rag = SimpleRAG(k=3)
result = rag(question="What are the main features of DSPy?")
print(f"Answer: {result.answer}")
\`\`\`

### Step 4: Adding Custom Logic and Validation

\`\`\`python
from typing import Literal
import re

class FactualQA(dspy.Signature):
    """Answer questions with confidence and citations."""
    
    question: str = dspy.InputField()
    answer: str = dspy.OutputField(desc="Concise factual answer")
    confidence: Literal["high", "medium", "low"] = dspy.OutputField()
    needs_search: bool = dspy.OutputField(desc="Whether web search would help")

class SmartQA(dspy.Module):
    def __init__(self):
        self.initial_answer = dspy.Predict(FactualQA)
        self.web_search = dspy.Retrieve(k=3)  # Simulated web search
        self.final_answer = dspy.ChainOfThought("context, question -> answer")
    
    def forward(self, question):
        # Get initial assessment
        initial = self.initial_answer(question=question)
        
        # If low confidence or needs search, use retrieval
        if initial.confidence == "low" or initial.needs_search:
            context = self.web_search(question).passages
            return self.final_answer(context=context, question=question)
        
        # Otherwise, return initial answer
        return initial

# Usage
smart_qa = SmartQA()

# Question that doesn't need search
result1 = smart_qa(question="What is 2 + 2?")
print(f"Q: What is 2 + 2?")
print(f"A: {result1.answer}")
print(f"Confidence: {result1.confidence}")
print(f"Needs search: {result1.needs_search}\\n")

# Question that needs search
result2 = smart_qa(question="What is the current population of Tokyo?")
print(f"Q: What is the current population of Tokyo?")
print(f"A: {result2.answer}")
\`\`\`

### Step 5: Optimization - Making It Better

\`\`\`python
# Create training data (even 20-50 examples work!)
trainset = [
    dspy.Example(
        question="What is the capital of France?",
        answer="Paris"
    ).with_inputs("question"),
    dspy.Example(
        question="What is 15% of 200?",
        answer="30"
    ).with_inputs("question"),
    dspy.Example(
        question="Who wrote Romeo and Juliet?",
        answer="William Shakespeare"
    ).with_inputs("question"),
    # Add more examples...
]

# Define what makes a good answer
def qa_metric(gold, pred, trace=None):
    # Simple exact match for demonstration
    return gold.answer.lower() in pred.answer.lower()

# Optimize the program
from dspy.teleprompt import BootstrapFewShot

optimizer = BootstrapFewShot(metric=qa_metric, max_bootstrapped_demos=3)
optimized_qa = optimizer.compile(cot_qa, trainset=trainset)

# Now use the optimized version
result = optimized_qa(question="What is the largest planet in our solar system?")
print(f"Answer: {result.answer}")
\`\`\`

### Step 6: Putting It All Together - Production-Ready Example

Here's a complete, production-ready example:

\`\`\`python
import dspy
from typing import List, Literal

# Configure DSPy
dspy.configure(
    lm=dspy.LM('openai/gpt-4o-mini'),
    trace=[]  # Enable tracing for debugging
)

class QuestionType(dspy.Signature):
    """Classify the type of question."""
    question: str = dspy.InputField()
    question_type: Literal["factual", "mathematical", "reasoning", "opinion"] = dspy.OutputField()

class MathSolver(dspy.Signature):
    """Solve mathematical problems step by step."""
    question: str = dspy.InputField()
    steps: List[str] = dspy.OutputField(desc="List of solution steps")
    answer: float = dspy.OutputField()

class AdvancedQA(dspy.Module):
    """An advanced QA system that routes questions to specialized handlers."""
    
    def __init__(self):
        # Question classifier
        self.classifier = dspy.Predict(QuestionType)
        
        # Specialized handlers
        self.factual_qa = dspy.ChainOfThought("question -> answer")
        self.math_solver = dspy.ChainOfThought(MathSolver)
        self.reasoning_qa = dspy.ChainOfThought("question -> reasoning, answer")
        self.opinion_qa = dspy.Predict("question -> perspective: str, disclaimer: str")
    
    def forward(self, question: str):
        # Classify question type
        question_type = self.classifier(question=question).question_type
        
        # Route to appropriate handler
        if question_type == "factual":
            return self.factual_qa(question=question)
        elif question_type == "mathematical":
            return self.math_solver(question=question)
        elif question_type == "reasoning":
            return self.reasoning_qa(question=question)
        else:  # opinion
            return self.opinion_qa(question=question)

# Create and test the system
advanced_qa = AdvancedQA()

test_questions = [
    "What is the capital of Japan?",
    "If I have 3 apples and buy 5 more, how many do I have?",
    "Why do we see stars at night but not during the day?",
    "What is the best programming language?"
]

for question in test_questions:
    print(f"\\nQuestion: {question}")
    result = advanced_qa(question)
    
    # Handle different output types
    if hasattr(result, 'steps'):
        print(f"Steps: {result.steps}")
        print(f"Answer: {result.answer}")
    elif hasattr(result, 'reasoning'):
        print(f"Reasoning: {result.reasoning}")
        print(f"Answer: {result.answer}")
    elif hasattr(result, 'perspective'):
        print(f"Perspective: {result.perspective}")
        print(f"Disclaimer: {result.disclaimer}")
    else:
        print(f"Answer: {result.answer}")
\`\`\`

### Exercise: Build Your Own Module

Try creating a module that:
1. Takes a topic as input
2. Generates 3 questions about that topic
3. Answers each question
4. Summarizes the key points

\`\`\`python
class TopicExplorer(dspy.Module):
    def __init__(self):
        # TODO: Initialize your sub-modules
        pass
    
    def forward(self, topic: str):
        # TODO: Implement the logic
        pass

# Solution provided in exercises/module_1_exercise_solution.py
\`\`\`

## 📋 Module 1 Summary

### What You've Learned

✅ **DSPy Philosophy**: Programming > Prompting
- Declarative specifications instead of prompt engineering
- Modular, composable, and optimizable systems

✅ **Core Components**:
- **Signatures**: Define what you want
- **Modules**: Implement how to get it
- **Optimizers**: Make it better with data
- **Programs**: Complete AI applications

✅ **Environment Setup**:
- Installing DSPy and dependencies
- Configuring language models
- Setting up development tools

✅ **Practical Skills**:
- Building basic QA systems
- Adding reasoning with ChainOfThought
- Creating custom modules
- Optimizing with training data

### Quick Reference Card

\`\`\`python
# Essential DSPy Commands

# 1. Configuration
import dspy
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

# 2. Basic Prediction
predict = dspy.Predict("input -> output")
result = predict(input="...")

# 3. Chain of Thought
cot = dspy.ChainOfThought("question -> answer")
result = cot(question="...")
# Access: result.reasoning, result.answer

# 4. Custom Module Template
class MyModule(dspy.Module):
    def __init__(self):
        self.component = dspy.Predict("...")
    
    def forward(self, **kwargs):
        return self.component(**kwargs)

# 5. Optimization
optimizer = dspy.BootstrapFewShot(metric=my_metric)
optimized = optimizer.compile(program, trainset=data)

# 6. Saving/Loading
optimized.save("my_program.json")
loaded = MyModule()
loaded.load("my_program.json")
\`\`\`

### Practice Exercises

1. **Exercise 1.1**: Modify the basic QA system to return confidence scores
2. **Exercise 1.2**: Create a module that translates text and explains cultural context
3. **Exercise 1.3**: Build a fact-checker that cites sources
4. **Exercise 1.4**: Implement a module that generates quiz questions from text

### Additional Resources

- 📚 [DSPy Documentation](https://dspy-docs.vercel.app)
- 💻 [GitHub Repository](https://github.com/stanfordnlp/dspy)
- 🎥 [DSPy YouTube Tutorials](https://www.youtube.com/@stanfordmlsystems)
- 💬 [Discord Community](https://discord.gg/RVzqW8JjYH)

### What's Next?

In Module 2, we'll dive deep into:
- Advanced signature patterns
- All built-in DSPy modules
- Complex module composition
- Real-world integration patterns

Ready to continue? Let's explore the full power of DSPy programming!
`,
  exercises: [
    {
      id: 'dspy-m1-ex1',
      title: 'Build Your First AI Assistant',
      description: 'Create a helpful AI assistant that can answer questions about various topics',
      difficulty: 'easy',
      type: 'implementation',
      instructions: `## Context
You've been asked to create a simple AI assistant for a local library. The librarians want a tool that can answer basic questions from visitors about general knowledge topics, library hours, and book recommendations.

## Your Goal
Build a DSPy program that can:
1. Answer factual questions (like "What is the capital of France?")
2. Provide helpful responses about library-related queries
3. Be polite and informative in its responses

## Requirements
- Set up DSPy with a language model
- Create a question-answering system
- Test it with at least 3 different types of questions:
  - A geography question
  - A library hours question
  - A book recommendation request

## Starting Point
Begin by importing DSPy and setting up your environment. Think about what makes a good library assistant - it should be accurate, helpful, and friendly.`,
      hints: [
        'Start simple: First just get DSPy imported and configured. Use dspy.configure() with an LM.',
        'For the QA system, think about the signature. What goes in? (question) What comes out? (answer)',
        'dspy.Predict is perfect for simple question->answer tasks. The signature format is "input -> output".',
        'Test with different questions to see how your assistant responds. Does it maintain a helpful tone?',
        'If your responses seem too brief, you can add a description to your signature like "question -> helpful_answer"',
      ],
      solution: `import dspy

# Configure DSPy with a language model
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

# Create a helpful library assistant
# We use a descriptive field name to encourage helpful responses
library_assistant = dspy.Predict("question -> helpful_answer")

# Test with different types of questions
test_questions = [
    "What is the capital of France?",
    "What time does the library close on weekends?",
    "Can you recommend a good mystery novel?"
]

print("Library Assistant Demo\\n" + "="*40)
for question in test_questions:
    result = library_assistant(question=question)
    print(f"\\nVisitor: {question}")
    print(f"Assistant: {result.helpful_answer}")`,
    },
    {
      id: 'dspy-m1-ex2',
      title: 'Math Tutor with Step-by-Step Explanations',
      description: 'Build an AI math tutor that shows its work',
      difficulty: 'medium',
      type: 'implementation',
      instructions: `## Context
A local tutoring center wants to create an AI math tutor that doesn't just give answers, but explains HOW to solve problems step-by-step. Students have been struggling because most AI tools just give the final answer without showing the work.

## Your Goal
Create a DSPy program that:
1. Takes math word problems as input
2. Shows clear step-by-step reasoning
3. Provides the final answer
4. Can handle different types of math problems (arithmetic, percentages, basic algebra)

## Requirements
- Use Chain of Thought reasoning to show problem-solving steps
- Test with at least 3 different math problems of varying difficulty
- Make sure the explanations are clear and educational

## Starting Point
Think about how a human tutor would explain a problem. They would:
- Break down what the problem is asking
- Identify the relevant information
- Show each calculation step
- Verify the answer makes sense

## Test Problems to Try
1. "If a store sells 25 apples per day and operates 7 days a week, how many apples does it sell in 4 weeks?"
2. "Sarah has $50. She spends 30% on books. How much money does she have left?"
3. "A train travels at 60 mph. How long will it take to travel 180 miles?"`,
      hints: [
        'Chain of Thought is perfect for this! It automatically generates reasoning steps.',
        'The basic usage is dspy.ChainOfThought("question -> answer")',
        'When you call it, you get back both reasoning and answer. Access them with result.reasoning and result.answer',
        'Try formatting the output nicely. Maybe add a header for "Step-by-step solution:" before the reasoning',
        'You can make the reasoning clearer by using a more descriptive signature like "math_problem -> detailed_solution"',
        'Consider wrapping this in a function that formats the output nicely for students',
      ],
      solution: `import dspy

# Configure DSPy
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

# Create a math tutor that shows its work
math_tutor = dspy.ChainOfThought("math_problem -> detailed_solution")

# Test problems
test_problems = [
    "If a store sells 25 apples per day and operates 7 days a week, how many apples does it sell in 4 weeks?",
    "Sarah has $50. She spends 30% on books. How much money does she have left?",
    "A train travels at 60 mph. How long will it take to travel 180 miles?"
]

print("AI Math Tutor\\n" + "="*40)

for i, problem in enumerate(test_problems, 1):
    print(f"\\nProblem {i}: {problem}")
    print("-" * 40)
    
    result = math_tutor(math_problem=problem)
    
    print("Step-by-step solution:")
    print(result.reasoning)
    print(f"\\nFinal Answer: {result.detailed_solution}")
    print("\\n" + "="*40)`,
    },
    {
      id: 'dspy-m1-ex3',
      title: 'Smart Recipe Assistant Module',
      description: 'Build a custom DSPy module that helps with cooking',
      difficulty: 'hard',
      type: 'implementation',
      instructions: `## Context
A popular cooking app wants to create an AI assistant that can help users in the kitchen. They've noticed that some questions need detailed explanations (like "How do I make bread from scratch?") while others just need quick answers (like "How many teaspoons in a tablespoon?").

## Your Goal
Create a custom DSPy module called "RecipeAssistant" that:
1. Takes a cooking-related question as input
2. Intelligently decides whether the question needs:
   - A quick, direct answer (for simple conversions, temperatures, etc.)
   - Detailed step-by-step instructions (for recipes, techniques, etc.)
3. Uses the appropriate DSPy component for each type
4. Returns a helpful response tailored to the question type

## Requirements
- Your module must inherit from dspy.Module
- Implement the __init__ and forward methods
- Use at least two different DSPy components (e.g., Predict and ChainOfThought)
- Include logic to choose between different response strategies
- Test with both simple and complex cooking questions

## Test Questions to Try
1. "How many cups are in a quart?"
2. "How do I properly caramelize onions?"
3. "What temperature should chicken be cooked to?"
4. "How do I make homemade pasta from scratch?"

## Why This Matters
This exercise teaches you how to create sophisticated AI systems by combining multiple DSPy components. This is a key skill for building production-ready applications.`,
      hints: [
        'Start by creating a class that inherits from dspy.Module. The basic structure is: class RecipeAssistant(dspy.Module):',
        "In __init__, initialize the different components you'll need. Consider having one for checking question complexity and others for answering.",
        'For the complexity checker, try: self.complexity_checker = dspy.Predict("cooking_question -> is_complex: bool")',
        'You\'ll want a simple predictor for quick answers: self.quick_answer = dspy.Predict("question -> answer")',
        'For detailed instructions, use Chain of Thought: self.detailed_answer = dspy.ChainOfThought("question -> detailed_instructions")',
        'In the forward method, first check the complexity, then route to the appropriate answering component',
        'The forward method should accept the question and return a structured response',
        'Consider returning a dictionary with the response and metadata about which method was used',
      ],
      solution: `import dspy

# Configure DSPy
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

class RecipeAssistant(dspy.Module):
    """A smart cooking assistant that adapts its response style."""
    
    def __init__(self):
        # Initialize sub-modules
        self.complexity_checker = dspy.Predict("cooking_question -> needs_detailed_response: bool")
        self.quick_answer = dspy.Predict("question -> quick_answer")
        self.detailed_instructor = dspy.ChainOfThought("cooking_question -> step_by_step_instructions")
    
    def forward(self, question):
        # Check if the question needs a detailed response
        complexity_result = self.complexity_checker(cooking_question=question)
        
        # Route to appropriate answering strategy
        if complexity_result.needs_detailed_response:
            # Use Chain of Thought for complex cooking questions
            result = self.detailed_instructor(cooking_question=question)
            response_type = "detailed"
            answer = result.step_by_step_instructions
            reasoning = result.reasoning
        else:
            # Use simple prediction for quick answers
            result = self.quick_answer(question=question)
            response_type = "quick"
            answer = result.quick_answer
            reasoning = None
        
        # Return structured response
        return {
            "question": question,
            "response_type": response_type,
            "answer": answer,
            "reasoning": reasoning
        }

# Create and test the assistant
assistant = RecipeAssistant()

# Test with different types of questions
test_questions = [
    "How many cups are in a quart?",
    "How do I properly caramelize onions?",
    "What temperature should chicken be cooked to?",
    "How do I make homemade pasta from scratch?"
]

print("Recipe Assistant Demo\\n" + "="*50)

for question in test_questions:
    result = assistant(question=question)
    
    print(f"\\nQ: {result['question']}")
    print(f"Response type: {result['response_type']}")
    print(f"A: {result['answer']}")
    
    if result['reasoning']:
        print(f"\\nThought process: {result['reasoning']}")
    
    print("-" * 50)`,
    },
    {
      id: 'dspy-m1-ex4',
      title: 'Educational Content Creator',
      description: 'Build a module that creates educational content automatically',
      difficulty: 'hard',
      type: 'implementation',
      instructions: `## Context
An online education platform wants to automatically generate educational content for various subjects. Teachers spend hours creating study materials, and they need an AI system that can help generate comprehensive learning resources from just a topic name.

## Your Goal
Create a DSPy module called "StudyGuideCreator" that:
1. Takes a subject topic as input (e.g., "Photosynthesis", "World War II", "Pythagorean Theorem")
2. Automatically generates:
   - 3 key learning objectives
   - 3 important questions students should be able to answer
   - Detailed answers to each question (with reasoning shown)
   - A concise summary suitable for a study guide
3. Organizes everything into a structured format

## Requirements
- Create custom DSPy signatures for structured outputs
- Combine multiple DSPy components (Predict, ChainOfThought)
- Demonstrate the power of composition in DSPy
- Output should be well-organized and educational

## Test Topics to Try
1. "The Water Cycle"
2. "Basic Algebra"
3. "The Solar System"
4. "Shakespearean Sonnets"

## Why This Matters
This exercise shows how DSPy can build complex, multi-step AI applications. You'll learn to:
- Define custom signatures for structured data
- Orchestrate multiple AI components
- Create practical applications that solve real problems

## Bonus Challenge
Can you extend your module to also generate:
- A practice quiz question based on the content
- Common misconceptions about the topic`,
      hints: [
        'Start with custom signatures. For learning objectives, try: class LearningObjectives(dspy.Signature): with topic as input and objectives as output',
        'DSPy can output structured data. For lists, use: objectives: list[str] = dspy.OutputField(desc="3 learning objectives")',
        'Build your module step by step. First get question generation working, then add answering, then summarization',
        'For question generation, consider: self.question_gen = dspy.Predict("topic -> study_questions: list[str]")',
        'Use ChainOfThought for answering questions to show the reasoning: self.answerer = dspy.ChainOfThought("question -> answer")',
        'In forward(), orchestrate the flow: generate objectives → generate questions → answer questions → summarize',
        'To format the output nicely, return a dictionary with all the components organized',
        'For the summary, pass all the generated content as context: self.summarizer = dspy.Predict("topic, objectives, qa_pairs -> summary")',
      ],
      solution: `import dspy

# Configure DSPy
dspy.configure(lm=dspy.LM('openai/gpt-4o-mini'))

# Define custom signatures for structured outputs
class LearningObjectives(dspy.Signature):
    """Generate learning objectives for a topic."""
    topic: str = dspy.InputField()
    objectives: list[str] = dspy.OutputField(desc="3 specific learning objectives")

class StudyQuestions(dspy.Signature):
    """Generate important study questions."""
    topic: str = dspy.InputField()
    objectives: str = dspy.InputField()
    questions: list[str] = dspy.OutputField(desc="3 important study questions")

class StudySummary(dspy.Signature):
    """Create a concise study guide summary."""
    topic: str = dspy.InputField()
    content: str = dspy.InputField()
    summary: str = dspy.OutputField(desc="Concise summary for study guide")

class StudyGuideCreator(dspy.Module):
    """Creates comprehensive study guides from topics."""
    
    def __init__(self):
        # Initialize all components
        self.objective_generator = dspy.Predict(LearningObjectives)
        self.question_generator = dspy.Predict(StudyQuestions)
        self.answer_generator = dspy.ChainOfThought("question, topic -> answer")
        self.summarizer = dspy.Predict(StudySummary)
    
    def forward(self, topic: str):
        # Step 1: Generate learning objectives
        objectives_result = self.objective_generator(topic=topic)
        objectives = objectives_result.objectives
        
        # Step 2: Generate study questions based on objectives
        questions_result = self.question_generator(
            topic=topic,
            objectives=str(objectives)
        )
        questions = questions_result.questions
        
        # Step 3: Answer each question with reasoning
        qa_pairs = []
        for question in questions:
            answer_result = self.answer_generator(
                question=question,
                topic=topic
            )
            qa_pairs.append({
                "question": question,
                "answer": answer_result.answer,
                "reasoning": answer_result.reasoning
            })
        
        # Step 4: Create summary
        content = f"Objectives: {objectives}\\nQ&A: {qa_pairs}"
        summary_result = self.summarizer(
            topic=topic,
            content=content
        )
        
        # Return organized study guide
        return {
            "topic": topic,
            "learning_objectives": objectives,
            "qa_pairs": qa_pairs,
            "summary": summary_result.summary
        }

# Create and test the study guide creator
creator = StudyGuideCreator()

# Test with a topic
topic = "The Water Cycle"
study_guide = creator(topic=topic)

# Display the study guide
print(f"STUDY GUIDE: {study_guide['topic']}")
print("="*60)

print("\\nLEARNING OBJECTIVES:")
for i, obj in enumerate(study_guide['learning_objectives'], 1):
    print(f"{i}. {obj}")

print("\\nKEY QUESTIONS AND ANSWERS:")
for i, qa in enumerate(study_guide['qa_pairs'], 1):
    print(f"\\nQ{i}: {qa['question']}")
    print(f"Reasoning: {qa['reasoning']}")
    print(f"Answer: {qa['answer']}")

print("\\nSUMMARY:")
print(study_guide['summary'])`,
    },
  ],
  quiz: {
    id: 'dspy-m1-quiz',
    title: 'Module 1: Introduction to DSPy - Knowledge Check',
    description: 'Test your understanding of DSPy fundamentals',
    passingScore: 7,
    questionsPerQuiz: 10,
    questions: [], // This will be populated from questionBank randomly
    questionBank: [
      // Core Philosophy & Concepts (Questions 1-20)
      {
        id: 'q1',
        question:
          'What is the main philosophical difference between DSPy and traditional prompting?',
        choices: [
          'DSPy uses more advanced language models',
          'DSPy treats LLM calls as function calls rather than text completions',
          'DSPy only works with OpenAI models',
          'DSPy requires more manual prompt engineering',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy\'s core philosophy is "Programming, Not Prompting" - it treats language model interactions as structured function calls with defined inputs and outputs.',
      },
      {
        id: 'q2',
        question: 'Which of the following is NOT a key benefit of DSPy?',
        choices: [
          'Modularity and composability',
          'Model portability across different LLMs',
          'Manual prompt optimization',
          'Systematic performance improvement through data',
        ],
        correctAnswer: 2,
        explanation:
          'DSPy specifically eliminates the need for manual prompt optimization. Instead, it provides systematic optimization through data.',
      },
      {
        id: 'q3',
        question: 'What does a DSPy Signature define?',
        choices: [
          'The programming language syntax',
          'The input/output interface for language model calls',
          'The model configuration parameters',
          'The optimization algorithm to use',
        ],
        correctAnswer: 1,
        explanation:
          'Signatures in DSPy define the input/output interface for language model calls, specifying expected inputs and outputs.',
      },
      {
        id: 'q4',
        question: 'DSPy transforms language model interactions from what to what?',
        choices: [
          'Code to prompts',
          'Text completions to function calls',
          'Function calls to text completions',
          'Prompts to assembly code',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy transforms LLM calls from text completions into function calls with clear interfaces.',
      },
      {
        id: 'q5',
        question: "What is the core insight behind DSPy's approach?",
        choices: [
          'Use more complex prompts',
          'Treat LM interactions like any other programming interface',
          'Always use the largest available model',
          'Write prompts in multiple languages',
        ],
        correctAnswer: 1,
        explanation:
          'The breakthrough insight is to treat language model interactions like any other programming interface.',
      },
      {
        id: 'q6',
        question: 'In DSPy, instead of crafting prompts, you:',
        choices: [
          'Write more detailed instructions',
          'Declare what you want your system to do',
          'Use pre-built templates',
          'Train your own model',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy allows you to declare what you want your system to do rather than crafting specific prompts.',
      },
      {
        id: 'q7',
        question: 'What does "Programming, Not Prompting" mean in DSPy?',
        choices: [
          'You write code instead of natural language',
          'You use programming languages other than Python',
          'You structure LM interactions as code components rather than text manipulation',
          'You avoid using language models entirely',
        ],
        correctAnswer: 2,
        explanation:
          'It means structuring language model interactions as programmable components rather than manual text manipulation.',
      },
      {
        id: 'q8',
        question: 'How does DSPy handle prompt optimization?',
        choices: [
          'Manual tweaking by developers',
          'A/B testing different versions',
          'Automatic optimization based on data and specifications',
          'Using pre-written prompt libraries',
        ],
        correctAnswer: 2,
        explanation:
          'DSPy automatically optimizes prompts based on your specifications and training data.',
      },
      {
        id: 'q9',
        question: 'What makes DSPy systems more maintainable than traditional prompting?',
        choices: [
          'Shorter code files',
          'Modularity and systematic optimization',
          'Fewer dependencies',
          'Simpler algorithms',
        ],
        correctAnswer: 1,
        explanation:
          "DSPy's modularity and systematic optimization make systems more maintainable than fragile prompt-based approaches.",
      },
      {
        id: 'q10',
        question: 'DSPy is particularly valuable for:',
        choices: [
          'One-time scripting tasks',
          'Simple question-answering',
          'Complex, production-ready AI systems',
          'Basic text generation',
        ],
        correctAnswer: 2,
        explanation:
          'DSPy excels at building complex, production-ready AI systems that need reliability and systematic improvement.',
      },
      {
        id: 'q11',
        question: 'What problem does DSPy solve regarding prompt engineering?',
        choices: [
          'Makes prompts longer and more detailed',
          'Eliminates the brittleness and manual effort of prompt crafting',
          'Requires specialized prompt engineering skills',
          'Uses only pre-trained prompts',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy eliminates the brittleness and manual effort associated with traditional prompt engineering.',
      },
      {
        id: 'q12',
        question:
          'In traditional prompting, what happens when you need to adapt to a new use case?',
        choices: [
          'Simple configuration changes',
          'Automatic adaptation',
          'Often requires starting from scratch',
          'Just change the model',
        ],
        correctAnswer: 2,
        explanation:
          'Traditional prompting often requires starting from scratch when adapting to new use cases due to poor modularity.',
      },
      {
        id: 'q13',
        question: "DSPy's declarative approach means you:",
        choices: [
          'Write detailed step-by-step instructions',
          'Specify what you want, not how to get it',
          'Use only declarative programming languages',
          'Declare variables before using them',
        ],
        correctAnswer: 1,
        explanation:
          'Declarative specifications in DSPy mean you define what you want, not how to get it.',
      },
      {
        id: 'q14',
        question: 'What does DSPy do with your specifications?',
        choices: [
          'Stores them for later use',
          'Automatically generates optimal prompts',
          'Sends them directly to the language model',
          'Converts them to natural language',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy automatically generates optimal prompts based on your specifications and training data.',
      },
      {
        id: 'q15',
        question: 'How does DSPy handle system composition?',
        choices: [
          'Through complex inheritance hierarchies',
          'Using global configuration files',
          'By building complex systems from simple, reusable components',
          'Through manual integration code',
        ],
        correctAnswer: 2,
        explanation:
          'DSPy enables building complex systems from simple, reusable components that can be optimized independently.',
      },
      {
        id: 'q16',
        question: 'What is systematic optimization in DSPy?',
        choices: [
          'Manual tuning based on intuition',
          'Random trial and error',
          'Data-driven improvement using training examples',
          'Using the newest language models',
        ],
        correctAnswer: 2,
        explanation:
          'Systematic optimization means improving performance through data and automatic techniques rather than manual tweaking.',
      },
      {
        id: 'q17',
        question: 'Why is DSPy compared to using high-level programming languages vs assembly?',
        choices: [
          'Both are low-level approaches',
          'DSPy provides high-level abstractions over low-level prompt engineering',
          'Assembly is faster than high-level languages',
          'Both require detailed knowledge of hardware',
        ],
        correctAnswer: 1,
        explanation:
          'Just as high-level languages abstract away assembly details, DSPy abstracts away prompt engineering details.',
      },
      {
        id: 'q18',
        question: 'What happens to prompt engineering in a DSPy system?',
        choices: [
          'It becomes more important',
          'It requires specialized skills',
          'It becomes automatic and data-driven',
          'It is eliminated entirely',
        ],
        correctAnswer: 2,
        explanation:
          'In DSPy, prompt engineering becomes automatic and data-driven rather than manual and intuitive.',
      },
      {
        id: 'q19',
        question: 'DSPy enables what kind of development workflow?',
        choices: [
          'Iterative prompt testing',
          'Rapid prototyping with systematic improvement',
          'Manual optimization cycles',
          'Template-based development',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy enables rapid prototyping combined with systematic, data-driven improvement of AI systems.',
      },
      {
        id: 'q20',
        question: 'What makes DSPy particularly suitable for production environments?',
        choices: [
          'Faster inference speed',
          'Lower computational costs',
          'Reliability, maintainability, and systematic optimization',
          'Smaller model requirements',
        ],
        correctAnswer: 2,
        explanation:
          "DSPy's focus on reliability, maintainability, and systematic optimization makes it ideal for production systems.",
      },

      // DSPy Modules & Components (Questions 21-40)
      {
        id: 'q21',
        question:
          'Which DSPy module would you use to add step-by-step reasoning to your predictions?',
        choices: ['dspy.Predict', 'dspy.ChainOfThought', 'dspy.ReAct', 'dspy.Retrieve'],
        correctAnswer: 1,
        explanation:
          'dspy.ChainOfThought automatically adds reasoning steps to predictions, making the LM show its work.',
      },
      {
        id: 'q22',
        question: 'What is the most basic DSPy module for making predictions?',
        choices: ['dspy.Generate', 'dspy.Predict', 'dspy.Complete', 'dspy.Basic'],
        correctAnswer: 1,
        explanation:
          'dspy.Predict is the most basic module for making predictions with language models in DSPy.',
      },
      {
        id: 'q23',
        question: 'In DSPy, what does the forward() method in a custom module define?',
        choices: [
          'The optimization strategy',
          'The input validation rules',
          'The main logic and execution flow',
          'The output formatting',
        ],
        correctAnswer: 2,
        explanation:
          'The forward() method defines the main logic and execution flow of how inputs are processed to produce outputs.',
      },
      {
        id: 'q24',
        question: 'Which DSPy module combines reasoning with action-taking capabilities?',
        choices: ['dspy.Think', 'dspy.ReAct', 'dspy.Action', 'dspy.Agent'],
        correctAnswer: 1,
        explanation:
          'dspy.ReAct (Reasoning and Acting) combines reasoning with action-taking capabilities for more complex tasks.',
      },
      {
        id: 'q25',
        question: 'What is the purpose of dspy.Retrieve?',
        choices: [
          'To get data from databases',
          'To fetch information from knowledge bases or search systems',
          'To recover from errors',
          'To retrieve model parameters',
        ],
        correctAnswer: 1,
        explanation:
          'dspy.Retrieve is used to fetch relevant information from knowledge bases or search systems.',
      },
      {
        id: 'q26',
        question: 'How do you create a custom DSPy module?',
        choices: [
          'Inherit from dspy.Module and implement forward()',
          'Use the @dspy.module decorator',
          'Call dspy.create_module()',
          'Write a configuration file',
        ],
        correctAnswer: 0,
        explanation:
          'Custom DSPy modules are created by inheriting from dspy.Module and implementing the forward() method.',
      },
      {
        id: 'q27',
        question: 'What does dspy.ChainOfThought add to basic predictions?',
        choices: [
          'Multiple choice options',
          'Confidence scores',
          'Step-by-step reasoning before the final answer',
          'Alternative solutions',
        ],
        correctAnswer: 2,
        explanation:
          'ChainOfThought adds intermediate reasoning steps that lead to the final answer, improving transparency.',
      },
      {
        id: 'q28',
        question: 'In DSPy modules, what are sub-modules?',
        choices: [
          'Smaller versions of main modules',
          'Components that can be composed together to build complex systems',
          'Error handling mechanisms',
          'Configuration parameters',
        ],
        correctAnswer: 1,
        explanation:
          'Sub-modules are reusable components that can be composed together to build more complex DSPy systems.',
      },
      {
        id: 'q29',
        question: 'What happens when you call a DSPy module?',
        choices: [
          'It compiles the code',
          'It executes the forward() method',
          'It optimizes the parameters',
          'It validates the inputs',
        ],
        correctAnswer: 1,
        explanation:
          'When you call a DSPy module, it executes the forward() method which contains the main logic.',
      },
      {
        id: 'q30',
        question: 'How do DSPy modules handle state?',
        choices: [
          'Through global variables',
          'Using database connections',
          'Via instance variables and learnable parameters',
          'Through configuration files',
        ],
        correctAnswer: 2,
        explanation:
          'DSPy modules handle state through instance variables and learnable parameters that can be optimized.',
      },
      {
        id: 'q31',
        question: 'What is the benefit of using dspy.ChainOfThought over dspy.Predict?',
        choices: [
          'Faster execution',
          'Lower token usage',
          'More interpretable reasoning process',
          'Better error handling',
        ],
        correctAnswer: 2,
        explanation:
          'ChainOfThought provides a more interpretable reasoning process by showing intermediate thinking steps.',
      },
      {
        id: 'q32',
        question: 'Can DSPy modules be nested inside other modules?',
        choices: [
          'No, they must be used independently',
          'Yes, modules can contain other modules as sub-components',
          'Only in special cases',
          'Only with specific decorators',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy modules can be nested and composed, with modules containing other modules as sub-components.',
      },
      {
        id: 'q33',
        question: 'What is the role of signatures in DSPy modules?',
        choices: [
          'Code documentation',
          'Error handling',
          'Defining the input/output interface',
          'Performance optimization',
        ],
        correctAnswer: 2,
        explanation:
          'Signatures define the input/output interface that DSPy modules should follow.',
      },
      {
        id: 'q34',
        question: 'How does DSPy handle module initialization?',
        choices: [
          'Automatic initialization with default values',
          'Through __init__ method like regular Python classes',
          'Using configuration files',
          'Via dependency injection',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy modules are initialized through the __init__ method like regular Python classes.',
      },
      {
        id: 'q35',
        question: 'What makes DSPy modules different from regular Python functions?',
        choices: [
          'They run faster',
          'They can be optimized and contain learnable parameters',
          'They use different syntax',
          'They require special imports',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy modules can be optimized and contain learnable parameters, unlike regular Python functions.',
      },
      {
        id: 'q36',
        question: 'In a DSPy pipeline, how do modules communicate?',
        choices: [
          'Through global variables',
          'Via message passing',
          'Through input/output data flow',
          'Using shared memory',
        ],
        correctAnswer: 2,
        explanation:
          'DSPy modules communicate through structured input/output data flow between components.',
      },
      {
        id: 'q37',
        question: "What is the advantage of DSPy's modular approach?",
        choices: [
          'Smaller file sizes',
          'Faster compilation',
          'Reusability, testability, and independent optimization',
          'Lower memory usage',
        ],
        correctAnswer: 2,
        explanation:
          'The modular approach provides reusability, testability, and the ability to optimize components independently.',
      },
      {
        id: 'q38',
        question: 'How do you specify what a DSPy module should do?',
        choices: [
          'Through detailed comments',
          'Using natural language descriptions',
          'Via signatures and the forward() method implementation',
          'Through configuration parameters',
        ],
        correctAnswer: 2,
        explanation:
          'You specify what a module should do through signatures and the forward() method implementation.',
      },
      {
        id: 'q39',
        question: 'Can DSPy modules be used with different language models?',
        choices: [
          'No, they are model-specific',
          'Yes, they provide model portability',
          'Only with models from the same family',
          'Only after retraining',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy modules provide model portability, allowing the same code to work with different language models.',
      },
      {
        id: 'q40',
        question: 'What happens to DSPy modules during optimization?',
        choices: [
          'They are recompiled',
          'Their parameters and prompts are automatically improved',
          'They are replaced with newer versions',
          'Their structure is modified',
        ],
        correctAnswer: 1,
        explanation:
          'During optimization, DSPy modules have their parameters and prompts automatically improved based on data.',
      },

      // Signatures & Configuration (Questions 41-60)
      {
        id: 'q41',
        question: 'Which of these is a valid DSPy signature format?',
        choices: [
          '"question -> answer"',
          '"input: output"',
          '"question => answer"',
          '"question | answer"',
        ],
        correctAnswer: 0,
        explanation: 'DSPy uses the arrow notation "input -> output" for signatures.',
      },
      {
        id: 'q42',
        question: 'Which command is used to configure DSPy with a language model?',
        choices: ['dspy.setup()', 'dspy.configure()', 'dspy.initialize()', 'dspy.connect()'],
        correctAnswer: 1,
        explanation:
          'dspy.configure() is used to set up DSPy with language models and other global configurations.',
      },
      {
        id: 'q43',
        question: 'How do you specify multiple inputs in a DSPy signature?',
        choices: [
          '"input1 + input2 -> output"',
          '"input1, input2 -> output"',
          '"input1 & input2 -> output"',
          '"input1; input2 -> output"',
        ],
        correctAnswer: 1,
        explanation:
          'Multiple inputs in DSPy signatures are specified with commas: "input1, input2 -> output".',
      },
      {
        id: 'q44',
        question: 'What does dspy.configure() typically include?',
        choices: [
          'Database connections',
          'Language model and retriever configurations',
          'User interface settings',
          'File system paths',
        ],
        correctAnswer: 1,
        explanation:
          'dspy.configure() typically includes language model and retriever configurations.',
      },
      {
        id: 'q45',
        question:
          'In a signature like "context, question -> answer", what does "context" represent?',
        choices: [
          'The execution environment',
          'Background information or relevant documents',
          'The previous conversation',
          'System configuration',
        ],
        correctAnswer: 1,
        explanation:
          'In this signature, "context" represents background information or relevant documents for answering the question.',
      },
      {
        id: 'q46',
        question: 'Can DSPy signatures include type hints?',
        choices: [
          'No, only string names',
          'Yes, signatures can include field descriptions and types',
          'Only for outputs',
          'Only for built-in types',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy signatures can include field descriptions and type information for better specification.',
      },
      {
        id: 'q47',
        question: "What happens if you don't call dspy.configure()?",
        choices: [
          'DSPy uses default settings',
          "You'll get runtime errors when trying to use language models",
          'DSPy will prompt you to configure',
          'It automatically detects available models',
        ],
        correctAnswer: 1,
        explanation:
          "Without dspy.configure(), you'll get runtime errors because DSPy doesn't know which language model to use.",
      },
      {
        id: 'q48',
        question: 'How do you specify the expected format of outputs in DSPy signatures?',
        choices: [
          'Through type annotations',
          'Using format strings',
          'Via field descriptions and examples',
          'Through regular expressions',
        ],
        correctAnswer: 2,
        explanation:
          'Output formats in DSPy signatures are specified through field descriptions and examples.',
      },
      {
        id: 'q49',
        question: 'Can you change the DSPy configuration during runtime?',
        choices: [
          "No, it's set once at startup",
          'Yes, you can call dspy.configure() multiple times',
          'Only during debugging',
          'Only for certain parameters',
        ],
        correctAnswer: 1,
        explanation:
          'You can call dspy.configure() multiple times to change the configuration during runtime.',
      },
      {
        id: 'q50',
        question: 'What is the purpose of field descriptions in DSPy signatures?',
        choices: [
          'Code documentation',
          'Performance optimization',
          'To provide context and constraints for language model generation',
          'Error handling',
        ],
        correctAnswer: 2,
        explanation:
          'Field descriptions provide context and constraints to help the language model generate appropriate outputs.',
      },
      {
        id: 'q51',
        question: 'In DSPy, signatures are used to:',
        choices: [
          'Sign code for security',
          'Define the expected input/output structure for language model calls',
          'Create digital certificates',
          'Validate user permissions',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy signatures define the expected input/output structure for language model interactions.',
      },
      {
        id: 'q52',
        question: 'How do signatures help with prompt generation?',
        choices: [
          'They replace prompts entirely',
          'They provide structure for automatic prompt creation',
          'They make prompts shorter',
          'They translate prompts to different languages',
        ],
        correctAnswer: 1,
        explanation:
          'Signatures provide structure that DSPy uses to automatically create appropriate prompts.',
      },
      {
        id: 'q53',
        question: "What's the difference between a signature and a module in DSPy?",
        choices: [
          "No difference, they're the same",
          'Signatures define interfaces, modules implement logic',
          'Signatures are for inputs, modules are for outputs',
          'Signatures are faster than modules',
        ],
        correctAnswer: 1,
        explanation:
          'Signatures define the input/output interface, while modules implement the actual logic and processing.',
      },
      {
        id: 'q54',
        question: 'Can one DSPy module use multiple signatures?',
        choices: [
          'No, each module has exactly one signature',
          'Yes, modules can use different signatures for different sub-components',
          'Only in advanced configurations',
          'Only with special decorators',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy modules can use different signatures for different sub-components and operations.',
      },
      {
        id: 'q55',
        question: 'How do you specify optional inputs in DSPy signatures?',
        choices: [
          'Using square brackets [optional]',
          'Through field descriptions and default values',
          'With question marks ?optional',
          'Using parentheses (optional)',
        ],
        correctAnswer: 1,
        explanation:
          'Optional inputs are specified through field descriptions and default values in DSPy.',
      },
      {
        id: 'q56',
        question: 'What role do signatures play in DSPy optimization?',
        choices: [
          'They are ignored during optimization',
          'They guide the optimization process by defining what to optimize for',
          'They slow down optimization',
          'They only matter for error handling',
        ],
        correctAnswer: 1,
        explanation:
          'Signatures guide optimization by defining the input/output structure that optimizers work with.',
      },
      {
        id: 'q57',
        question: 'In DSPy, can signatures be nested or hierarchical?',
        choices: [
          'No, they must be flat',
          'Yes, complex signatures can have nested structure',
          'Only two levels deep',
          'Only with special syntax',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy supports complex signatures with nested structures for sophisticated input/output specifications.',
      },
      {
        id: 'q58',
        question: 'How do signatures help with model portability?',
        choices: [
          "They don't affect portability",
          'They provide a consistent interface regardless of the underlying model',
          'They make models run faster',
          'They reduce memory usage',
        ],
        correctAnswer: 1,
        explanation:
          'Signatures provide a consistent interface that works across different language models.',
      },
      {
        id: 'q59',
        question: "What happens if your data doesn't match the signature specification?",
        choices: [
          'DSPy automatically fixes the data',
          'You get validation errors or poor performance',
          'DSPy ignores the signature',
          'The signature adapts automatically',
        ],
        correctAnswer: 1,
        explanation:
          'Mismatched data can lead to validation errors or poor performance since the model expects the specified format.',
      },
      {
        id: 'q60',
        question: 'Can DSPy signatures include examples?',
        choices: [
          'No, only input/output names',
          "Yes, examples help guide the language model's behavior",
          'Only for debugging',
          'Only in development mode',
        ],
        correctAnswer: 1,
        explanation:
          "DSPy signatures can include examples that help guide and improve the language model's behavior.",
      },

      // Optimization & Training (Questions 61-80)
      {
        id: 'q61',
        question:
          'What is the minimum number of training examples typically needed for DSPy optimization?',
        choices: ['1000-5000 examples', '500-1000 examples', '200-500 examples', '20-200 examples'],
        correctAnswer: 3,
        explanation:
          'DSPy is designed to work effectively with minimal data. Often just 20-200 examples are sufficient.',
      },
      {
        id: 'q62',
        question: 'What is the primary purpose of DSPy optimizers like BootstrapFewShot?',
        choices: [
          'To speed up model inference',
          'To reduce computational costs',
          'To automatically improve program performance using data',
          'To handle model version updates',
        ],
        correctAnswer: 2,
        explanation:
          'DSPy optimizers automatically improve program performance by finding better prompts and configurations based on data.',
      },
      {
        id: 'q63',
        question: 'Which optimizer is commonly used for few-shot learning in DSPy?',
        choices: ['BootstrapFewShot', 'AdamOptimizer', 'GradientDescent', 'RandomSearch'],
        correctAnswer: 0,
        explanation:
          'BootstrapFewShot is a commonly used DSPy optimizer for improving few-shot learning performance.',
      },
      {
        id: 'q64',
        question: 'What does MIPRO stand for in DSPy optimization?',
        choices: [
          'Multi-Instance Prompt Optimization',
          'Machine Intelligence Prompt Resource Optimization',
          'Model-Independent Performance and Robustness Optimization',
          'Minimal Instruction Prompt Optimization',
        ],
        correctAnswer: 2,
        explanation:
          'MIPRO stands for Model-Independent Performance and Robustness Optimization in DSPy.',
      },
      {
        id: 'q65',
        question: 'How does DSPy optimization differ from traditional machine learning training?',
        choices: [
          'It requires more data',
          'It optimizes prompts and examples rather than model weights',
          "It's faster but less accurate",
          'It only works with specific models',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy optimization focuses on optimizing prompts and examples rather than training model weights.',
      },
      {
        id: 'q66',
        question: 'What do you need to define for DSPy optimization to work?',
        choices: [
          'Learning rate and batch size',
          'Training data and evaluation metrics',
          'Network architecture',
          'Hardware specifications',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy optimization requires training data and evaluation metrics to guide the improvement process.',
      },
      {
        id: 'q67',
        question: 'Can DSPy optimization work without labeled training data?',
        choices: [
          'Yes, it can use unsupervised methods',
          'No, it always requires labels',
          'Only with very large models',
          'Only for simple tasks',
        ],
        correctAnswer: 0,
        explanation:
          'DSPy can use various optimization approaches, including some that work with minimal or no labeled data.',
      },
      {
        id: 'q68',
        question: 'What happens during the bootstrapping process in DSPy?',
        choices: [
          'The system restarts automatically',
          'Better examples are generated from initial predictions',
          'New models are downloaded',
          'Configuration files are updated',
        ],
        correctAnswer: 1,
        explanation:
          'Bootstrapping in DSPy generates better examples and demonstrations from initial predictions to improve performance.',
      },
      {
        id: 'q69',
        question: 'How long does typical DSPy optimization take?',
        choices: [
          'Several days',
          'Minutes to hours, depending on the task',
          'Always less than one minute',
          'Weeks of continuous training',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy optimization typically takes minutes to hours, depending on task complexity and dataset size.',
      },
      {
        id: 'q70',
        question: 'What can be optimized in a DSPy program?',
        choices: [
          'Only the prompts',
          'Prompts, examples, and module configurations',
          'Only the model weights',
          'Only the input data',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy can optimize prompts, few-shot examples, and various module configurations.',
      },
      {
        id: 'q71',
        question: 'How do you evaluate whether DSPy optimization is working?',
        choices: [
          'By looking at the generated prompts',
          'Using defined metrics on validation data',
          'By measuring execution speed',
          'Through manual inspection',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy optimization effectiveness is evaluated using defined metrics on validation data.',
      },
      {
        id: 'q72',
        question: 'Can you optimize different parts of a DSPy pipeline independently?',
        choices: [
          'No, the entire pipeline must be optimized together',
          'Yes, individual modules can be optimized separately',
          "Only if they don't share data",
          'Only with special configurations',
        ],
        correctAnswer: 1,
        explanation: 'DSPy allows individual modules in a pipeline to be optimized independently.',
      },
      {
        id: 'q73',
        question: 'What is the role of validation data in DSPy optimization?',
        choices: [
          "It's not needed",
          'To evaluate and guide the optimization process',
          'Only for final testing',
          'To train the language model',
        ],
        correctAnswer: 1,
        explanation:
          'Validation data is used to evaluate different optimization candidates and guide the improvement process.',
      },
      {
        id: 'q74',
        question: 'How does DSPy handle overfitting during optimization?',
        choices: [
          "It doesn't address overfitting",
          'Through validation sets and regularization techniques',
          'By using smaller models',
          'By reducing training data',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy uses validation sets and various regularization techniques to prevent overfitting during optimization.',
      },
      {
        id: 'q75',
        question: 'Can DSPy optimization improve an already good system?',
        choices: [
          'No, it only helps bad systems',
          'Yes, it can provide incremental improvements to well-performing systems',
          'Only if the system was built with DSPy originally',
          'Only for specific types of tasks',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy optimization can provide incremental improvements even to systems that are already performing well.',
      },
      {
        id: 'q76',
        question: "What's the difference between BootstrapFewShot and other optimizers?",
        choices: [
          'BootstrapFewShot is faster',
          'BootstrapFewShot focuses on generating better few-shot examples',
          'BootstrapFewShot works with any model',
          'BootstrapFewShot requires less data',
        ],
        correctAnswer: 1,
        explanation:
          'BootstrapFewShot specifically focuses on generating and selecting better few-shot examples for improved performance.',
      },
      {
        id: 'q77',
        question: 'How do you know when DSPy optimization is complete?',
        choices: [
          'When it reaches 100% accuracy',
          'When performance plateaus or meets target metrics',
          'After a fixed number of iterations',
          'When the prompts stop changing',
        ],
        correctAnswer: 1,
        explanation:
          'Optimization is typically complete when performance plateaus on validation data or meets target metrics.',
      },
      {
        id: 'q78',
        question: 'Can DSPy optimization work with custom evaluation metrics?',
        choices: [
          'No, only built-in metrics',
          'Yes, you can define custom metrics for your specific task',
          'Only for classification tasks',
          'Only with numerical metrics',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy supports custom evaluation metrics tailored to your specific task requirements.',
      },
      {
        id: 'q79',
        question: 'What happens to the original DSPy program after optimization?',
        choices: [
          "It's completely replaced",
          'The structure remains the same, but parameters and prompts are improved',
          "It's merged with the optimized version",
          "It's stored as a backup",
        ],
        correctAnswer: 1,
        explanation:
          'After optimization, the program structure remains the same, but internal parameters and prompts are improved.',
      },
      {
        id: 'q80',
        question:
          'Is it possible to optimize DSPy programs for multiple objectives simultaneously?',
        choices: [
          'No, only single objectives',
          'Yes, multi-objective optimization is supported',
          'Only for specific optimizers',
          'Only with custom implementations',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy supports multi-objective optimization where you can balance multiple performance criteria.',
      },

      // Practical Usage & Best Practices (Questions 81-100)
      {
        id: 'q81',
        question: 'What type of problems is DSPy NOT ideal for?',
        choices: [
          'Complex multi-stage pipelines',
          'Production AI systems requiring reliability',
          'One-off scripts or simple experiments',
          'Applications needing systematic improvement',
        ],
        correctAnswer: 2,
        explanation:
          'DSPy is designed for robust systems. For simple one-off scripts, traditional prompting might be more appropriate.',
      },
      {
        id: 'q82',
        question: 'When should you consider using DSPy over traditional prompting?',
        choices: [
          'For any language model task',
          'When you need reliability, modularity, and systematic improvement',
          'Only for research projects',
          'When you have unlimited computational resources',
        ],
        correctAnswer: 1,
        explanation:
          'DSPy is ideal when you need reliability, modularity, and systematic improvement in your AI systems.',
      },
      {
        id: 'q83',
        question: "What's a good first step when starting a DSPy project?",
        choices: [
          'Write complex multi-stage pipelines',
          'Start with a simple signature and basic module',
          'Immediately begin optimization',
          'Set up multiple language models',
        ],
        correctAnswer: 1,
        explanation:
          "It's best to start simple with a basic signature and module, then gradually build complexity.",
      },
      {
        id: 'q84',
        question: 'How should you handle errors in DSPy programs?',
        choices: [
          'Ignore them during development',
          'Implement proper error handling and validation',
          'Let DSPy handle all errors automatically',
          'Only handle errors in production',
        ],
        correctAnswer: 1,
        explanation:
          'Like any robust software, DSPy programs should implement proper error handling and validation.',
      },
      {
        id: 'q85',
        question: "What's the recommended approach for testing DSPy modules?",
        choices: [
          'Only test the final output',
          'Test individual modules and the complete pipeline',
          'Testing is not necessary with DSPy',
          'Only test after optimization',
        ],
        correctAnswer: 1,
        explanation:
          'Best practice is to test both individual modules and the complete pipeline for reliability.',
      },
      {
        id: 'q86',
        question: 'How do you debug issues in DSPy programs?',
        choices: [
          'Check intermediate outputs and use logging',
          'Only look at final results',
          'Restart the optimization process',
          'Switch to a different language model',
        ],
        correctAnswer: 0,
        explanation:
          'Debugging DSPy programs involves checking intermediate outputs and using proper logging techniques.',
      },
      {
        id: 'q87',
        question: "What's important when choosing training data for DSPy optimization?",
        choices: [
          'More data is always better',
          'Quality and representativeness matter more than quantity',
          'Only use synthetic data',
          "Data format doesn't matter",
        ],
        correctAnswer: 1,
        explanation:
          'Quality and representativeness of training data are more important than sheer quantity in DSPy.',
      },
      {
        id: 'q88',
        question: 'How do you maintain DSPy systems in production?',
        choices: [
          'They maintain themselves automatically',
          'Regular monitoring, evaluation, and reoptimization as needed',
          'Only update the language models',
          'Maintenance is not required',
        ],
        correctAnswer: 1,
        explanation:
          'Production DSPy systems require regular monitoring, evaluation, and reoptimization as conditions change.',
      },
      {
        id: 'q89',
        question: "What's a key advantage of DSPy's modular design for team development?",
        choices: [
          'Faster compilation',
          'Different team members can work on different modules independently',
          'Reduced code conflicts',
          'Automatic code review',
        ],
        correctAnswer: 1,
        explanation:
          'The modular design allows different team members to work on separate modules independently.',
      },
      {
        id: 'q90',
        question: 'How do you handle version control with DSPy programs?',
        choices: [
          'Only version the final optimized programs',
          'Version both the program code and optimization artifacts',
          'DSPy handles versioning automatically',
          'Version control is not needed',
        ],
        correctAnswer: 1,
        explanation:
          'Best practice is to version both the program code and optimization artifacts for reproducibility.',
      },
      {
        id: 'q91',
        question: 'What should you consider when scaling DSPy applications?',
        choices: [
          'Only computational resources',
          'Caching, parallel processing, and resource management',
          'Just use bigger models',
          'Scaling is automatic',
        ],
        correctAnswer: 1,
        explanation:
          'Scaling DSPy applications requires considering caching, parallel processing, and resource management.',
      },
      {
        id: 'q92',
        question: 'How do you ensure reproducibility in DSPy experiments?',
        choices: [
          'Run experiments multiple times',
          'Set random seeds and version all components',
          'Use the same computer',
          'Reproducibility is guaranteed',
        ],
        correctAnswer: 1,
        explanation:
          'Reproducibility requires setting random seeds and versioning all components including data and configurations.',
      },
      {
        id: 'q93',
        question: "What's important when documenting DSPy programs?",
        choices: [
          'Only document the final results',
          'Document signatures, module purposes, and optimization settings',
          'Documentation is optional',
          'Only document errors',
        ],
        correctAnswer: 1,
        explanation:
          'Good documentation should cover signatures, module purposes, and optimization settings for maintainability.',
      },
      {
        id: 'q94',
        question: 'How do you handle different environments (dev/staging/prod) with DSPy?',
        choices: [
          'Use the same configuration everywhere',
          'Separate configurations and potentially different optimization artifacts',
          'Only optimize in production',
          "Environments don't matter",
        ],
        correctAnswer: 1,
        explanation:
          'Different environments should have separate configurations and potentially different optimization artifacts.',
      },
      {
        id: 'q95',
        question: "What's a good strategy for monitoring DSPy systems in production?",
        choices: [
          'No monitoring needed',
          'Track performance metrics, error rates, and data drift',
          'Only monitor system resources',
          'Monitor only when problems occur',
        ],
        correctAnswer: 1,
        explanation:
          'Production monitoring should track performance metrics, error rates, and potential data drift.',
      },
      {
        id: 'q96',
        question: 'How do you handle updates to language models in DSPy systems?',
        choices: [
          'Updates are automatic',
          'Test thoroughly and potentially reoptimize with new models',
          'Never update models',
          'Just change the configuration',
        ],
        correctAnswer: 1,
        explanation:
          'Model updates require thorough testing and potentially reoptimization since different models may behave differently.',
      },
      {
        id: 'q97',
        question: "What's the best way to share DSPy modules across projects?",
        choices: [
          'Copy and paste code',
          'Create reusable packages with proper interfaces',
          'Use global variables',
          'Email code snippets',
        ],
        correctAnswer: 1,
        explanation:
          'Creating reusable packages with proper interfaces is the best way to share DSPy modules across projects.',
      },
      {
        id: 'q98',
        question: 'How do you handle sensitive data in DSPy programs?',
        choices: [
          'Data sensitivity is not a concern',
          'Implement proper data handling, privacy, and security measures',
          'Only use synthetic data',
          'DSPy automatically protects data',
        ],
        correctAnswer: 1,
        explanation:
          'Sensitive data requires proper handling, privacy measures, and security practices just like any software system.',
      },
      {
        id: 'q99',
        question: "What's important when migrating from traditional prompting to DSPy?",
        choices: [
          'Complete the migration in one step',
          'Gradual migration with performance comparison at each step',
          'Start completely from scratch',
          'Migration is not recommended',
        ],
        correctAnswer: 1,
        explanation:
          'A gradual migration approach with performance comparison at each step helps ensure successful transition.',
      },
      {
        id: 'q100',
        question: "What's the key to long-term success with DSPy?",
        choices: [
          'Using the newest models',
          'Building systematic processes for development, optimization, and maintenance',
          'Having the most data',
          'Working alone',
        ],
        correctAnswer: 1,
        explanation:
          'Long-term success with DSPy requires building systematic processes for development, optimization, and maintenance.',
      },
    ],
  },
};
